import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { DateField } from '@shared/data/entity/fields/DateField';
import { Field, castField } from '@shared/data/entity/fields/Field';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { PersonField } from '@shared/data/entity/fields/PersonField';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TextField } from '@shared/data/entity/fields/TextField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { omitTypeName } from '@shared/util/omitTypeName';

interface Props {
  workflowTemplate?: API.WorkflowTemplate;
  fields: FieldRecord;
  relatedCardComponent: RelatedCardComponent;
}

export function useFilterByComponents({
  workflowTemplate,
  fields,
  relatedCardComponent,
}: Props): API.FilterByComponent[] {
  return useMemo(() => {
    if (isNil(workflowTemplate)) {
      return [];
    }
    const filters =
      relatedCardComponent.listDefault?.filterByComponents?.filter(notNil).map(omitTypeName) ?? [];
    return filters
      .map(filter => getFilterByComponent(filter, workflowTemplate, fields))
      .filter(notNil);
  }, [workflowTemplate, fields, relatedCardComponent]);
}

function getFilterByComponent(
  filter: API.DynamicFilterByComponent,
  workflowTemplate: API.WorkflowTemplate,
  fields: FieldRecord,
): API.DynamicFilterByComponent | null {
  const dynamicComponentId =
    filter.value.containsCurrentWorkflowTemplateComponentValue?.componentId;

  if (isNil(dynamicComponentId)) {
    return {
      componentId: filter.componentId,
      value: {
        ...filter.value,
      },
    };
  } else {
    const component = workflowTemplate.components.find(item => item?.id === filter.componentId);
    const field = fields[dynamicComponentId];
    if (isNil(component) || isNil(field)) {
      return null;
    }
    return getFilterByComponentFromField(component, field);
  }
}

function getFilterByComponentFromField(
  component: API.AnyComponent,
  field: Field,
): API.DynamicFilterByComponent | null {
  if (isTagComponent(component)) {
    const tagField = castField(field, TagField);
    return tagField ? getTagComponentFilter(component, tagField) : null;
  }
  if (isRelatedCardComponent(component)) {
    const relatedEntityField = castField(field, RelatedEntityField);
    return relatedEntityField
      ? getRelatedEntityComponentFilter(component, relatedEntityField)
      : null;
  }
  if (isPersonComponent(component)) {
    const personField = castField(field, PersonField);
    return personField ? getPersonComponentFilter(component, personField) : null;
  }
  if (isTextComponent(component)) {
    const textField = castField(field, TextField);
    return textField ? getTextFieldFilter(component, textField) : null;
  }
  if (isDateComponent(component)) {
    const dateField = castField(field, DateField);
    return dateField ? getDateComponentFilter(component, dateField) : null;
  }
  if (isNumberComponent(component)) {
    const numberField = castField(field, NumberField);
    return numberField ? getNumberComponentFilter(component, numberField) : null;
  }
  return null;
}

function getTagComponentFilter(
  component: API.TagComponent,
  field: TagField,
): API.DynamicFilterByComponent | null {
  const values = field.tagIds;
  if (isEmpty(values)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: { values, negate: false },
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getRelatedEntityComponentFilter(
  component: API.RelatedCardComponent,
  field: RelatedEntityField,
): API.DynamicFilterByComponent | null {
  const values = field.items.map(item => item.id);
  if (isEmpty(values)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: { values, negate: false },
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getPersonComponentFilter(
  component: API.PersonComponent,
  field: PersonField,
): API.DynamicFilterByComponent | null {
  const values = field.users.filter(notNil).map(user => user.id);
  if (isEmpty(values)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: {
        values,
        negate: false,
      },
      containsString: null,
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getTextFieldFilter(
  component: API.TextComponent,
  field: TextField,
): API.DynamicFilterByComponent | null {
  const value = field.value;
  if (isEmpty(value)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: null,
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: { value },
    },
  };
}

function getDateComponentFilter(
  component: API.DateComponent,
  field: DateField,
): API.DynamicFilterByComponent | null {
  if (isNil(field.date)) {
    return null;
  }
  const day = moment(field.date);
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: null,
      dateIs: null,
      dateRange: {
        before: day.endOf('day').toISOString(),
        after: day.startOf('day').toISOString(),
      },
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getNumberComponentFilter(
  component: API.NumberComponent,
  field: NumberField,
): API.DynamicFilterByComponent | null {
  const value = field.value;
  if (isEmpty(value)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: null,
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: { value, operator: 'EQ' },
      stringWildcard: null,
    },
  };
}
